import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { gbp, repayment, stampDuty, borrowingEstimate } from "../lib/calc";

const Slider = ({ label, value, min, max, step, format, onChange, testId }) => {
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label className="text-sm font-medium text-neutral-600">{label}</label>
        <span data-testid={`${testId}-value`} className="font-display text-base font-semibold text-brand-ink">
          {format(value)}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        data-testid={testId}
        aria-label={label}
        className="btk-range mt-2"
        style={{ "--fill": `${fill}%` }}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
};

const pounds = (v) => gbp(v);
const years = (v) => `${v} yrs`;
const percent = (v) => `${Number(v).toFixed(2)}%`;

export const MiniEstimate = () => {
  const [price, setPrice] = useState(400000);
  const [deposit, setDeposit] = useState(60000);
  const [term, setTerm] = useState(30);
  const rate = 4.4;
  const r = useMemo(
    () => repayment({ price, deposit, rate, termYears: term, interestOnly: false }),
    [price, deposit, term]
  );

  return (
    <div data-testid="mini-estimate-card" className="rounded-3xl border border-black/5 bg-white p-7 shadow-[0_24px_64px_rgba(1,20,50,0.10)] sm:p-8">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-display text-lg font-bold text-brand-ink">60-second estimate</h3>
        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold text-brand">60-second estimate</span>
      </div>
      <div className="mt-6 space-y-5">
        <Slider label="Property price" value={price} min={80000} max={1500000} step={5000} format={pounds} onChange={setPrice} testId="mini-price-slider" />
        <Slider label="Deposit" value={deposit} min={5000} max={Math.min(600000, price)} step={2500} format={pounds} onChange={setDeposit} testId="mini-deposit-slider" />
        <Slider label="Mortgage term" value={term} min={5} max={40} step={1} format={years} onChange={setTerm} testId="mini-term-slider" />
      </div>
      <div className="mt-7 flex items-end justify-between gap-4 rounded-2xl bg-brand-ink p-5">
        <div>
          <p className="text-xs font-medium uppercase tracking-widest text-neutral-400">Estimated monthly</p>
          <p data-testid="mini-monthly-output" className="mt-1 font-display text-4xl font-bold text-white">
            {gbp(r.monthly)}
          </p>
        </div>
        <p className="pb-1 text-right text-xs leading-relaxed text-neutral-400">
          at {percent(rate)}
          <br />
          {Math.round(r.ltv)}% LTV
        </p>
      </div>
      <Link
        to="/calculators"
        data-testid="mini-full-calc-link"
        className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand transition-colors duration-200 hover:text-brand-dark"
      >
        Open the full calculators
        <ArrowRight size={15} weight="bold" />
      </Link>
      <p className="mt-3 text-[11px] leading-relaxed text-neutral-400">
        Illustrative only. Your actual rate depends on your circumstances — talk to us for the real figures.
      </p>
    </div>
  );
};

export const RepaymentCalculator = () => {
  const [price, setPrice] = useState(400000);
  const [deposit, setDeposit] = useState(60000);
  const [rate, setRate] = useState(4.4);
  const [term, setTerm] = useState(30);
  const [interestOnly, setInterestOnly] = useState(false);

  const r = useMemo(
    () => repayment({ price, deposit, rate, termYears: term, interestOnly }),
    [price, deposit, rate, term, interestOnly]
  );

  const stats = [
    ["Amount borrowed", gbp(r.principal), "calc-borrowed"],
    ["Loan to value", `${Math.round(r.ltv)}%`, "calc-ltv"],
    ["Total interest over term", gbp(r.totalInterest), "calc-interest"],
    ["Total repayable", gbp(r.totalRepayable), "calc-total"],
  ];

  return (
    <div data-testid="repayment-calculator" className="grid gap-10 lg:grid-cols-2 lg:gap-14">
      <div className="space-y-6">
        <Slider label="Property price" value={price} min={80000} max={2000000} step={5000} format={pounds} onChange={setPrice} testId="repay-price-slider" />
        <Slider label="Deposit" value={deposit} min={0} max={Math.min(800000, price)} step={2500} format={pounds} onChange={setDeposit} testId="repay-deposit-slider" />
        <Slider label="Interest rate" value={rate} min={0.5} max={10} step={0.05} format={percent} onChange={setRate} testId="repay-rate-slider" />
        <Slider label="Mortgage term" value={term} min={5} max={40} step={1} format={years} onChange={setTerm} testId="repay-term-slider" />
        <div className="flex gap-3 pt-1" role="group" aria-label="Repayment type">
          {[
            [false, "Capital & interest"],
            [true, "Interest only"],
          ].map(([val, label]) => (
            <button
              key={label}
              data-testid={val ? "repay-type-interest-only" : "repay-type-capital"}
              onClick={() => setInterestOnly(val)}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-200 border ${
                interestOnly === val
                  ? "border-brand bg-brand text-white"
                  : "border-black/10 bg-white text-neutral-600 hover:border-brand/40 hover:text-brand"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex flex-col">
        <div className="rounded-3xl bg-brand-ink p-8 sm:p-10">
          <p className="text-xs font-medium uppercase tracking-widest text-neutral-400">Estimated monthly payment</p>
          <p data-testid="repay-monthly-output" className="mt-2 font-display text-5xl font-bold text-white sm:text-6xl">
            {gbp(r.monthly)}
          </p>
          <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-white/10">
            {stats.map(([label, value, id]) => (
              <div key={id} className="bg-brand-ink p-4">
                <p className="text-[11px] font-medium uppercase tracking-wider text-neutral-500">{label}</p>
                <p data-testid={id} className="mt-1 font-display text-lg font-semibold text-neutral-100">
                  {value}
                </p>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-4 text-[11px] leading-relaxed text-neutral-400">
          Illustrative figures in seconds — then talk to a human about the real ones. Your home may be
          repossessed if you do not keep up repayments on your mortgage.
        </p>
      </div>
    </div>
  );
};

export const StampDutyCalculator = () => {
  const [price, setPrice] = useState(400000);
  const [buyerType, setBuyerType] = useState("standard");
  const { tax, effectiveRate } = useMemo(() => stampDuty(price, buyerType), [price, buyerType]);

  return (
    <div data-testid="stamp-duty-calculator" className="grid gap-10 lg:grid-cols-2 lg:gap-14">
      <div className="space-y-6">
        <Slider label="Property price" value={price} min={40000} max={2000000} step={5000} format={pounds} onChange={setPrice} testId="sdlt-price-slider" />
        <div>
          <p className="text-sm font-medium text-neutral-600">Buyer type</p>
          <div className="mt-3 flex flex-wrap gap-3" role="group" aria-label="Buyer type">
            {[
              ["standard", "Moving home / standard"],
              ["first-time", "First-time buyer"],
              ["additional", "Additional property"],
            ].map(([val, label]) => (
              <button
                key={val}
                data-testid={`sdlt-type-${val}`}
                onClick={() => setBuyerType(val)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,border-color] duration-200 border ${
                  buyerType === val
                    ? "border-brand bg-brand text-white"
                    : "border-black/10 bg-white text-neutral-600 hover:border-brand/40 hover:text-brand"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-[11px] leading-relaxed text-neutral-400">
          England &amp; Northern Ireland residential rates from April 2025, including the 5%
          additional-property surcharge and first-time buyer relief where applicable.
        </p>
      </div>
      <div className="rounded-3xl border border-black/5 bg-white p-8 shadow-[0_24px_64px_rgba(1,20,50,0.08)] sm:p-10">
        <p className="text-xs font-medium uppercase tracking-widest text-neutral-400">Stamp duty due</p>
        <p data-testid="sdlt-output" className="mt-2 font-display text-5xl font-bold text-brand sm:text-6xl">
          {gbp(tax)}
        </p>
        <p className="mt-4 text-sm text-neutral-500">
          Effective rate: <span data-testid="sdlt-rate" className="font-semibold text-brand-ink">{effectiveRate.toFixed(2)}%</span>
        </p>
      </div>
    </div>
  );
};

export const BorrowingCalculator = () => {
  const [income, setIncome] = useState(55000);
  const [secondIncome, setSecondIncome] = useState(0);
  const [deposit, setDeposit] = useState(60000);
  const { borrowing, maxPrice, ltv } = useMemo(
    () => borrowingEstimate({ income, secondIncome, deposit }),
    [income, secondIncome, deposit]
  );

  return (
    <div data-testid="borrowing-calculator" className="grid gap-10 lg:grid-cols-2 lg:gap-14">
      <div className="space-y-6">
        <Slider label="Your annual income" value={income} min={10000} max={300000} step={1000} format={pounds} onChange={setIncome} testId="borrow-income-slider" />
        <Slider label="Second applicant income" value={secondIncome} min={0} max={300000} step={1000} format={pounds} onChange={setSecondIncome} testId="borrow-income2-slider" />
        <Slider label="Deposit available" value={deposit} min={0} max={600000} step={2500} format={pounds} onChange={setDeposit} testId="borrow-deposit-slider" />
      </div>
      <div className="rounded-3xl bg-brand p-8 text-white shadow-[0_24px_64px_rgba(1,86,207,0.30)] sm:p-10">
        <p className="text-xs font-medium uppercase tracking-widest text-blue-100">You could borrow roughly</p>
        <p data-testid="borrow-output" className="mt-2 font-display text-5xl font-bold sm:text-6xl">
          {gbp(borrowing)}
        </p>
        <p className="mt-5 text-sm text-blue-100">
          Budget for a home up to{" "}
          <span data-testid="borrow-max-price" className="font-bold text-white">{gbp(maxPrice)}</span> with your
          deposit (<span data-testid="borrow-ltv">{Math.round(ltv)}% LTV</span>).
        </p>
        <p className="mt-4 text-[11px] leading-relaxed text-blue-200">
          Illustrative only — not a mortgage offer or financial advice. A rule-of-thumb guide
          (around 4.5x income). Lenders also weigh commitments, credit history and dependants — we'll
          give you an exact figure with a no-obligation call.
        </p>
      </div>
    </div>
  );
};
